<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Home -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Home</b>
      </a>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading geofences...</p>
    </div>

    <!-- Content (shown when not loading) -->
    <template v-else>
      <!-- Page Header -->
      <div class="schedule-header text-left">
        <div class="header-content">
          <div class="header-text">
            <h4 class="mb-2"><i class="fas fa-map-marker-alt mr-2"></i>Geofences</h4>
            <p class="mb-0">Manage safe zones and get alerts when your child arrives or leaves</p>
          </div>
          <button class="btn btn-add-schedule" @click="createGeofence">
            <i class="fas fa-plus-circle mr-2"></i>Create Geofence
          </button>
        </div>
      </div>

      <!-- Stats Summary -->
      <!--<div class="stats-container" v-if="geofences.length > 0">
        <div class="stat-card">
          <div class="stat-icon bg-primary">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ activeCount }}</div>
            <div class="stat-label">Active Zones</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-success">
            <i class="fas fa-map-marked-alt"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ geofences.length }}</div>
            <div class="stat-label">Total Geofences</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-info">
            <i class="fas fa-history"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalEvents }}</div>
            <div class="stat-label">Total Events</div>
          </div>
        </div>
      </div>-->

      <!-- Geofence List -->
      <div class="schedule-list-container" :style="{ 'width': '100%'}">
        <!-- Empty State -->
        <div v-if="geofences.length === 0" class="empty-state-modern">
          <div class="empty-icon">
            <i class="fas fa-map-marked-alt"></i>
          </div>
          <h5 class="empty-title">No Geofences Yet</h5>
          <p class="empty-text">Create your first geofence to get location alerts</p>
          <button class="btn btn-primary" @click="createGeofence">
            <i class="fas fa-plus-circle mr-1"></i>Create Your First Geofence
          </button>
        </div>

        <!-- Geofence List Items -->
        <div v-else class="schedules-list text-left">
          <div 
            v-for="geofence in geofences" 
            :key="geofence._id" 
            class="schedule-item" 
            :class="{ 'schedule-inactive': !geofence.isActive }"
          >
            <!-- Geofence Header -->
            <div class="schedule-item-header">
              <div class="schedule-title-section">
                <div class="schedule-icon" :style="{ background: geofence.color }">
                  <i :class="getIconClass(geofence.icon)"></i>
                </div>
                <div class="schedule-title-info">
                  <h5 class="schedule-name">{{ geofence.name }}</h5>
                  <div class="schedule-meta">
                    <span class="badge-modern badge-location-type">
                      <i class="fas fa-map-pin"></i> {{ geofence.icon }}
                    </span>
                    <span class="schedule-status-text" :class="geofence.isActive ? 'text-success' : 'text-muted'">
                      <i class="fas fa-circle"></i> {{ geofence.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="schedule-actions">
                <div class="custom-switch-modern">
                  <input 
                    type="checkbox" 
                    :id="'switch-' + geofence._id" 
                    :checked="geofence.isActive"
                    @change="toggleStatus(geofence)"
                    :disabled="geofence.isToggling"
                  >
                  <label :for="'switch-' + geofence._id"></label>
                </div>
              </div>
            </div>

            <!-- Geofence Content -->
            <div class="schedule-item-content">
              <!-- Address Section -->
              <div class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-map-pin"></i>
                  <span>Location</span>
                </div>
                <div class="location-address">
                  {{ geofence.location.address }}
                </div>
                <div class="location-coords">
                  <i class="fas fa-compass"></i>
                  {{ geofence.location.latitude.toFixed(6) }}, {{ geofence.location.longitude.toFixed(6) }}
                </div>
              </div>

              <!-- Details Section -->
              <div class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-info-circle"></i>
                  <span>Geofence Details</span>
                </div>
                <div class="time-slots-list">
                  <!-- Radius -->
                  <div class="time-slot-item radius-item">
                    <div class="time-slot-left">
                      <div class="time-slot-icon icon-radius">
                        <i class="fas fa-circle-notch"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Radius: {{ geofence.radius }}m</strong>
                        </div>
                        <div class="time-slot-label">
                          Detection zone size
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Notifications -->
                  <div class="time-slot-item notification-item">
                    <div class="time-slot-left">
                      <div class="time-slot-icon icon-notification">
                        <i class="fas fa-bell"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Notifications</strong>
                        </div>
                        <div class="time-slot-label">
                          <span v-if="geofence.notifyOnEntry">
                            <i class="fas fa-arrow-right text-success"></i> Entry
                          </span>
                          <span v-if="geofence.notifyOnEntry && geofence.notifyOnExit"> & </span>
                          <span v-if="geofence.notifyOnExit">
                            <i class="fas fa-arrow-left text-danger"></i> Exit
                          </span>
                          <span v-if="!geofence.notifyOnEntry && !geofence.notifyOnExit">
                            <i class="fas fa-bell-slash"></i> None
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Last Event -->
                  <div v-if="geofence.stats && geofence.stats.lastEventType" class="time-slot-item event-item">
                    <div class="time-slot-left">
                      <div class="time-slot-icon" :class="geofence.stats.lastEventType === 'entry' ? 'icon-entry' : 'icon-exit'">
                        <i :class="geofence.stats.lastEventType === 'entry' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Last Event</strong>
                        </div>
                        <div class="time-slot-label">
                          {{ geofence.stats.lastEventType === 'entry' ? 'Entered' : 'Left' }} 
                          {{ formatTime(geofence.stats.lastEventTime) }}
                        </div>
                      </div>
                    </div>
                    <div class="time-slot-right">
                      <span class="mode-badge" :class="geofence.stats.lastEventType === 'entry' ? 'mode-entry' : 'mode-exit'">
                        <i :class="geofence.stats.lastEventType === 'entry' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
                        {{ geofence.stats.lastEventType === 'entry' ? 'Entry' : 'Exit' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats Section -->
              <div v-if="geofence.stats" class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-chart-line"></i>
                  <span>Activity</span>
                </div>
                <div class="activity-stats">
                  <div class="activity-badge">
                    <i class="fas fa-history"></i>
                    {{ geofence.stats.totalEvents || 0 }} total events
                  </div>
                  <button class="btn-view-history" @click="viewGeofenceHistory(geofence)">
                    <i class="fas fa-list"></i> View History
                  </button>
                </div>
              </div>
            </div>

            <!-- Geofence Footer -->
            <div class="schedule-item-footer">
              <button 
                class="btn btn-sm btn-outline-edit" 
                @click="editGeofence(geofence)"
                :disabled="geofence.isDeleting"
              >
                <i class="fas fa-edit mr-1"></i>Edit
              </button>
              <button 
                class="btn btn-sm btn-outline-delete" 
                @click="confirmDelete(geofence)"
                :disabled="geofence.isDeleting"
              >
                <span v-if="geofence.isDeleting">
                  <i class="fas fa-spinner fa-spin mr-1"></i>Deleting...
                </span>
                <span v-else>
                  <i class="fas fa-trash-alt mr-1"></i>Delete
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="modal-overlay" 
      @click.self="cancelDelete"
    >
      <div class="modal-content">
        <div class="modal-header-custom">
          <h6>
            <i class="fas fa-exclamation-triangle text-danger mr-2"></i>
            Confirm Delete
          </h6>
        </div>
        <div class="modal-body-custom">
          <p>Are you sure you want to delete geofence <strong>"{{ geofenceToDelete?.name }}"</strong>?</p>
          <p class="text-muted small mb-0">This action cannot be undone. History will be preserved.</p>
        </div>
        <div class="modal-footer-custom">
          <button 
            class="btn btn-secondary btn-sm" 
            @click="cancelDelete"
            :disabled="isDeleting"
          >
            Cancel
          </button>
          <button 
            class="btn btn-danger btn-sm" 
            @click="deleteGeofence"
            :disabled="isDeleting"
          >
            <span v-if="isDeleting">
              <i class="fas fa-spinner fa-spin mr-1"></i>Deleting...
            </span>
            <span v-else>
              <i class="fas fa-trash mr-1"></i>Delete
            </span>
          </button>
        </div>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import GeofenceService from "@/services/GeofenceService";
import moment from "moment";
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";

export default {
  name: "GeofenceList",
  components: {
    KidHeader,
    KidFooter
  },
  data() {
    return {
      profileId: this.$route.query.profileId || "",
      serialNumber: this.$route.query.serialNumber || "",
      geofences: [],
      isLoading: false,
      showDeleteModal: false,
      geofenceToDelete: null,
      isDeleting: false
    };
  },
  computed: {
    activeCount() {
      return this.geofences.filter(g => g.isActive).length;
    },
    totalEvents() {
      return this.geofences.reduce((sum, g) => sum + (g.stats?.totalEvents || 0), 0);
    }
  },
  mounted() {
    this.loadGeofences();
  },
  methods: {
    async loadGeofences() {
      this.isLoading = true;
      try {
        const response = await GeofenceService.getGeofences(
          this.profileId,
          this.serialNumber
        );

        if (response.data.success) {
          this.geofences = (response.data.data || []).map(geofence => ({
            ...geofence,
            isToggling: false,
            isDeleting: false
          }));
        }
      } catch (error) {
        console.error("Error loading geofences:", error);
        this.$toast.error("Failed to load geofences");
      } finally {
        this.isLoading = false;
      }
    },

    async toggleStatus(geofence) {
      if (geofence.isToggling) return;

      geofence.isToggling = true;
      const previousStatus = geofence.isActive;
      geofence.isActive = !geofence.isActive;

      try {
        const response = await GeofenceService.toggleGeofenceStatus(
          geofence._id,
          {
            profileId: this.profileId,
            serialNumber: this.serialNumber
          }
        );

        if (response.data.success) {
          this.$toast.success(
            `Geofence ${geofence.isActive ? "activated" : "deactivated"}`
          );
        } else {
          geofence.isActive = previousStatus;
          this.$toast.error("Failed to update geofence status");
        }
      } catch (error) {
        geofence.isActive = previousStatus;
        console.error("Error toggling geofence:", error);
        this.$toast.error("Failed to update geofence status");
      } finally {
        geofence.isToggling = false;
      }
    },

    createGeofence() {
      this.$router.push({
        path: "/geofence/create",
        query: {
          profileId: this.profileId,
          serialNumber: this.serialNumber
        }
      });
    },

    editGeofence(geofence) {
      this.$router.push({
        path: "/geofence/create",
        query: {
          profileId: this.profileId,
          serialNumber: this.serialNumber,
          geofenceId: geofence._id,
          edit: true
        }
      });
    },

    viewGeofenceHistory(geofence) {
      this.$router.push({
        path: "/geofence/history",
        query: {
          profileId: this.profileId,
          serialNumber: this.serialNumber,
          geofenceId: geofence._id,
          geofenceName: geofence.name
        }
      });
    },

    confirmDelete(geofence) {
      this.geofenceToDelete = geofence;
      this.showDeleteModal = true;
    },

    cancelDelete() {
      this.showDeleteModal = false;
      this.geofenceToDelete = null;
    },

    async deleteGeofence() {
      if (!this.geofenceToDelete) return;

      this.isDeleting = true;

      try {
        const response = await GeofenceService.deleteGeofence(
          this.geofenceToDelete._id,
          {
            profileId: this.profileId,
            serialNumber: this.serialNumber
          }
        );

        if (response.data.success) {
          this.$toast.success("Geofence deleted successfully");
          
          this.geofences = this.geofences.filter(
            g => g._id !== this.geofenceToDelete._id
          );
          
          this.cancelDelete();
        } else {
          this.$toast.error("Failed to delete geofence");
        }
      } catch (error) {
        console.error("Error deleting geofence:", error);
        this.$toast.error("Failed to delete geofence");
      } finally {
        this.isDeleting = false;
      }
    },

    getIconClass(icon) {
      const iconMap = {
        home: "fas fa-home",
        school: "fas fa-school",
        hospital: "fas fa-hospital",
        store: "fas fa-shopping-cart",
        friend: "fas fa-user-friends",
        family: "fas fa-users",
        other: "fas fa-map-marker-alt"
      };
      return iconMap[icon] || iconMap.other;
    },

    formatTime(time) {
      if (!time) return "";
      return moment(time).fromNow();
    }
  }
};
</script>

<style scoped>
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 15px;
  font-size: 1rem;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Schedule Header (Geofence Header) */
.schedule-header {
  background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
  padding: 15px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.2);
  margin-bottom: 25px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-text h4 {
  color: white;
  font-weight: 700;
  font-size: 1.8rem;
  margin: 0;
}

.header-text p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
}

.btn-add-schedule {
  background: white;
  color: #10b981;
  border: none;
  padding: 12px 10px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-add-schedule:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  background: #f8f9fa;
  color: #059669;
}

/* Stats Container */
.stats-container {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  overflow-x: auto;
}

.stat-card {
  background: white;
  padding: 15px 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 200px;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 35px;
  height: 35px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.stat-icon.bg-primary {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.bg-success {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-icon.bg-info {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

/* Empty State */
.empty-state-modern {
  background: white;
  padding: 80px 40px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 3rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.empty-text {
  color: #6b7280;
  font-size: 1.05rem;
  margin-bottom: 30px;
}

/* Schedule List Items */
.schedules-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule-item {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  overflow: hidden;
}

.schedule-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  border-color: #10b981;
  transform: translateY(-2px);
}

.schedule-item.schedule-inactive {
  opacity: 0.7;
}

.schedule-item.schedule-inactive:hover {
  opacity: 1;
}

/* Schedule Item Header */
.schedule-item-header {
  padding: 10px;
  background: linear-gradient(to right, #f9fafb, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.schedule-title-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.schedule-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
}

.schedule-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 5px 0;
}

.schedule-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge-modern {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
}

.badge-location-type {
  background: #d1fae5;
  color: #065f46;
}

.schedule-status-text {
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.schedule-status-text i {
  font-size: 0.5rem;
}

/* Custom Switch Modern */
.custom-switch-modern {
  position: relative;
}

.custom-switch-modern input[type="checkbox"] {
  display: none;
}

.custom-switch-modern input[type="checkbox"]:disabled + label {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-switch-modern label {
  width: 60px;
  height: 32px;
  background: #d1d5db;
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
}

.custom-switch-modern label:after {
  content: '';
  width: 26px;
  height: 26px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-switch-modern input:checked + label {
  background: #10b981;
}

.custom-switch-modern input:checked + label:after {
  left: 31px;
}

/* Schedule Content */
.schedule-item-content {
  padding: 25px;
}

.schedule-section {
  margin-bottom: 15px;
}

.schedule-section:last-child {
  margin-bottom: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.section-label i {
  color: #10b981;
}

/* Location Info */
.location-address {
  font-size: 0.95rem;
  color: #1f2937;
  margin-bottom: 6px;
  font-weight: 500;
}

.location-coords {
  font-size: 0.85rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Time Slots List */
.time-slots-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.time-slot-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 4px solid #d1d5db;
  transition: all 0.2s ease;
}

.time-slot-item:hover {
  background: #f3f4f6;
  transform: translateX(4px);
}

.time-slot-item.radius-item {
  border-left-color: #3b82f6;
  background: #eff6ff;
}

.time-slot-item.notification-item {
  border-left-color: #8b5cf6;
  background: #f5f3ff;
}

.time-slot-item.event-item {
  border-left-color: #10b981;
  background: #f0fdf4;
}

.time-slot-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-slot-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.time-slot-icon.icon-radius {
  background: #bfdbfe;
  color: #1e40af;
}

.time-slot-icon.icon-notification {
  background: #ddd6fe;
  color: #6b21a8;
}

.time-slot-icon.icon-entry {
  background: #bbf7d0;
  color: #15803d;
}

.time-slot-icon.icon-exit {
  background: #fecaca;
  color: #991b1b;
}

.time-range {
  font-size: 0.75rem;
  color: #1f2937;
}

.time-slot-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
}

.mode-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.mode-badge.mode-entry {
  background: #10b981;
  color: white;
}

.mode-badge.mode-exit {
  background: #ef4444;
  color: white;
}

/* Activity Stats */
.activity-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.activity-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1f2937;
  font-weight: 600;
}

.btn-view-history {
  padding: 8px 16px;
  background: white;
  color: #10b981;
  border: 2px solid #10b981;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-view-history:hover {
  background: #10b981;
  color: white;
}

/* Schedule Footer */
.schedule-item-footer {
  padding: 10px 10px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-outline-edit {
  border: 2px solid #10b981;
  color: #10b981;
  background: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-outline-edit:hover:not(:disabled) {
  background: #10b981;
  color: white;
  transform: translateY(-2px);
}

.btn-outline-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline-delete {
  border: 2px solid #ef4444;
  color: #ef4444;
  background: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-outline-delete:hover:not(:disabled) {
  background: #ef4444;
  color: white;
  transform: translateY(-2px);
}

.btn-outline-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header-custom {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header-custom h6 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.modal-body-custom {
  padding: 20px;
}

.modal-body-custom p {
  margin-bottom: 12px;
  color: #333;
}

.modal-footer-custom {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #f9fafb;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .btn-add-schedule {
    width: 100%;
  }
  
  .stats-container {
    flex-direction: column;
  }
  
  .stat-card {
    min-width: 100%;
  }
  
  .schedule-item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .schedule-actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  
  .time-slot-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .time-slot-right {
    width: 100%;
  }
  
  .mode-badge {
    display: flex;
    width: 100%;
    justify-content: center;
  }
  
  .schedule-item-footer {
    justify-content: stretch;
  }
  
  .schedule-item-footer .btn {
    flex: 1;
  }

  .activity-stats {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-view-history {
    width: 100%;
  }
}
</style>